import { Overlay, OverlayConfig, OverlayRef, PositionStrategy } from '@angular/cdk/overlay';
import { ComponentPortal } from '@angular/cdk/portal';
import { ComponentRef, Directive, ElementRef, EventEmitter, inject, Input, Output, ViewContainerRef } from '@angular/core';

import { DatePickerRange, DatepickerPanel, Ucum } from '@treetop/shared/model';

import { DatepickerContentComponent } from './datepicker-content/datepicker-content.component';

export interface DatepickerControl {
  getConnectedOverlayOrigin(): ElementRef;
  getCurrentValue(): Date | Ucum | DatePickerRange | null;
  isDisabled(): boolean;
}

@Directive()
export class DatepickerPanelDirective implements DatepickerPanel {
  @Input() showRangeSelector = true;
  @Input() showCalendar = true;
  @Input() showCustomRangeSelector = false;
  @Input() set minDate(minDate: Date | 'today') {
    this._minDate = minDate === 'today' ? new Date() : minDate;
  }
  private _minDate: Date;

  private _viewContainerRef = inject(ViewContainerRef);
  private _overlay = inject(Overlay);

  private _overlayRef: OverlayRef | null;
  private _portal: ComponentPortal<DatepickerContentComponent>;
  private _overlayAttached = false;

  private _componentRef: ComponentRef<DatepickerContentComponent>;

  private datepickerInput: DatepickerControl;

  @Output() selectedDateChange = new EventEmitter<Date | Ucum>();
  @Output() selectedRangeChange = new EventEmitter<DatePickerRange>();

  public registerInput(datepickerInput: DatepickerControl): void {
    this.datepickerInput = datepickerInput;
  }

  public open(): void {
    if (this.datepickerInput.isDisabled()) {
      return;
    }

    this._destroyOverlay();

    let overlayRef = this._overlayRef;
    if (!overlayRef) {
      this._portal = new ComponentPortal(DatepickerContentComponent, this._viewContainerRef);
      overlayRef = this._overlay.create(this._getOverlayConfig());
      this._overlayRef = overlayRef;
    }

    if (this._overlayRef && !this._overlayRef.hasAttached()) {
      this._overlayAttached = true;
      this._componentRef = this._overlayRef.attach(this._portal);

      this._componentRef.instance.datepicker = this;
      const currentValue = this.datepickerInput?.getCurrentValue();
      if (currentValue !== null && currentValue !== undefined && typeof currentValue === 'object' && 'start' in currentValue) {
        const range = currentValue as DatePickerRange;
        this._componentRef.instance.rangeStart.set(range.start ?? null);
        this._componentRef.instance.rangeEnd.set(range.end ?? null);
      } else {
        this._componentRef.instance.selected.set(currentValue as Date | Ucum);
      }
      this._componentRef.instance.showCalendar.set(this.showCalendar);
      this._componentRef.instance.showRangeSelector.set(this.showRangeSelector);
      this._componentRef.instance.showCustomRangeSelector.set(this.showCustomRangeSelector);
      this._componentRef.instance.minDate.set(this._minDate);

      this._overlayRef.outsidePointerEvents().subscribe(() => this.closePanel());
    }
  }

  public selectedDateChanged(date: Date | Ucum): void {
    this.selectedDateChange.emit(date);
  }

  public selectedRangeChanged(range: DatePickerRange): void {
    this.selectedRangeChange.emit(range);
    setTimeout(() => this.closePanel(), 350);
  }

  private _getOverlayConfig(): OverlayConfig {
    return new OverlayConfig({
      positionStrategy: this._getOverlayPosition(),
      scrollStrategy: this._overlay.scrollStrategies.reposition(),
    });
  }

  private _getOverlayPosition(): PositionStrategy {
    return this._overlay
      .position()
      .flexibleConnectedTo(this.datepickerInput.getConnectedOverlayOrigin())
      .withFlexibleDimensions(false)
      .withPush(false)
      .withPositions([
        { originX: 'start', originY: 'bottom', overlayX: 'start', overlayY: 'top' },
        { originX: 'end', originY: 'bottom', overlayX: 'end', overlayY: 'top' },
        { originX: 'start', originY: 'top', overlayX: 'start', overlayY: 'bottom' },
        { originX: 'end', originY: 'top', overlayX: 'end', overlayY: 'bottom' },
      ]);
  }

  closePanel(): void {
    if (!this._overlayAttached) {
      return;
    }

    this._destroyOverlay();
  }

  private _destroyOverlay(): void {
    if (this._overlayRef) {
      this._overlayRef.dispose();
      this._overlayRef = null;
      this._componentRef = null;
    }
  }
}
